import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, Home, Users, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getLeaderboardBreakdown, getSoldPropertyCommissions } from '../../../api/kpi';
import { deleteLead } from '../../../api/lead';
import { hasAdminAccess } from "../../../utils/roleAccess";

/*
 * Dedicated drill-down page for a single Leaderboard figure.
 *
 * Reached from the Agency Leaderboard by clicking a stat card, a RESULTS row, or
 * an agent's number. Every metric gets its OWN page (its own title + a Back
 * button) rather than re-using My Listings / the Pipeline — so what you're
 * looking at is always unambiguous. The period (and optional agent) travel in the
 * URL and are sent to the backend with the exact same predicates the board uses,
 * so the list here always matches the number on the card.
 *
 * metric (route param): leads | listings | on_market | off_market | live | lost |
 *                       appraisals | sold | commission
 */

// Inline-styled, so these are CSS variables: light values in index.css, dark
// values in styles/theme-dark.css. Shared with CommissionsPage.
const C = {
  teal: '#55C5D0',
  darkGreen: 'var(--m-dark-green)',
  green: 'var(--m-green)',
  greenLight: 'var(--m-green-light)',
  surface1: 'var(--m-surface)',
  border: 'var(--m-border)',
  textPrimary: 'var(--m-ink)',
  textSecondary: 'var(--m-ink-2)',
  textMuted: 'var(--m-ink-3)',
  red: 'var(--m-red)',
};

const fmtMoney = (n) => {
  const num = typeof n === 'string' ? parseFloat(n.replace(/[^0-9.-]+/g, '')) : Number(n);
  if (!num || isNaN(num)) return '$0';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(num);
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Default titles when the caller didn't pass one.
const METRIC_TITLES = {
  leads: 'Selling Leads',
  listings: 'Listings Won',
  on_market: 'On-Market Listings',
  off_market: 'Off-Market Listings',
  live: 'Live Listings',
  lost: 'Lost (Withdrawn)',
  appraisals: 'Appraisals',
  sold: 'Sold',
  commission: 'Commission Earned',
};

const LeaderboardStatPage = () => {
  const { metric } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const startDate = params.get('start_date') || '';
  const endDate = params.get('end_date') || '';
  const period = params.get('period') || '';
  const agent = params.get('agent') || '';
  const agentName = params.get('agent_name') || '';
  const title = params.get('title') || METRIC_TITLES[metric] || 'Details';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Admins can prune no-good selling leads directly from this page. Gated to the
  // "leads" (Selling Leads) drill-down only.
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const canDelete = isAdmin && metric === 'leads';

  // A "sold"/"commission" list is the exact set of counted deals — the settlements
  // endpoint already returns those. Everything else comes from the breakdown
  // endpoint, which mirrors each figure's count predicates.
  const isCommissionKind = metric === 'sold' || metric === 'commission';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (isCommissionKind) {
          const res = await getSoldPropertyCommissions({ start_date: startDate, end_date: endDate });
          let list = Array.isArray(res.data) ? res.data : res.data?.results || [];
          if (agent) list = list.filter((r) => String(r.agent_id) === String(agent));
          list = list
            .map((r) => ({
              kind: 'sold',
              id: r.property_id,
              address: r.property || r.property_address || '—',
              agent_name: r.agent_name || '',
              amount: r.amount,
              date: r.settlement_date,
            }))
            .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
          if (!cancelled) setRows(list);
        } else {
          const res = await getLeaderboardBreakdown({
            metric,
            agent: agent || undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
          });
          if (!cancelled) setRows(res.data?.results || []);
        }
      } catch (e) {
        if (!cancelled) setError('Could not load this list. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [metric, agent, startDate, endDate, isCommissionKind]);

  const kind = isCommissionKind ? 'sold' : rows[0]?.kind || (metric === 'leads' || metric === 'appraisals' ? 'lead' : 'property');

  const openRow = (row) => {
    if (row.kind === 'lead') {
      if (row.contact_id) navigate(`/contacts/${row.contact_id}`);
      return;
    }
    // property / sold rows — crm ids are prefixed ("crm-123"); REA ids aren't linkable.
    const raw = String(row.id ?? '');
    const id = raw.startsWith('crm-') ? raw.slice(4) : raw.startsWith('rea-') ? null : raw;
    if (id) navigate(`/properties/${id}`);
  };

  const handleDelete = async (row, e) => {
    e.stopPropagation(); // don't trigger the row's navigate-to-contact click
    if (!row?.id) return;
    if (!window.confirm(`Delete the lead "${row.name || 'this lead'}"? This can't be undone.`)) return;
    setDeletingId(row.id);
    try {
      await deleteLead(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success('Lead deleted');
    } catch (err) {
      toast.error('Could not delete lead');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = useMemo(() => {
    const base =
      kind === 'lead'
        ? ['Name', 'Address', 'Stage', 'Priority', 'Source', 'Agent', 'Date']
        : kind === 'sold'
        ? ['Property', 'Agent', 'Commission', 'Settles']
        : ['Address', 'Suburb', 'Type', 'Price', 'Status', 'Agent', 'Listed'];
    return canDelete && kind === 'lead' ? [...base, ''] : base;
  }, [kind, canDelete]);

  const subtitle = [period, agentName].filter(Boolean).join(' · ');

  return (
    <div style={{ maxWidth: '100%', padding: '1rem' }}>
      {/* Header with Back button */}
      <button
        onClick={() => navigate('/commissions')}
        className="flex items-center gap-1.5 mb-3"
        style={{ background: 'transparent', border: 'none', color: C.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}
      >
        <ArrowLeft className="w-4 h-4" /> Back to Leaderboard
      </button>

      <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.darkGreen, margin: 0 }}>
            {agentName ? `${agentName} · ${title}` : title}
          </h1>
          {subtitle && <p style={{ fontSize: 13, color: C.textMuted, margin: '4px 0 0' }}>{subtitle}</p>}
        </div>
        {!loading && !error && (
          <span
            style={{ fontSize: 13, fontWeight: 600, color: C.green, background: C.greenLight, padding: '5px 12px', borderRadius: 999 }}
          >
            {rows.length} {rows.length === 1 ? 'result' : 'results'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: C.teal }} />
        </div>
      ) : error ? (
        <div style={{ background: C.surface1, borderRadius: 12, padding: '28px 16px', textAlign: 'center', color: C.textSecondary }}>
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ background: C.surface1, borderRadius: 12, padding: '40px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
          <div style={{ marginBottom: 8 }}>
            {kind === 'lead' ? <Users className="w-8 h-8 mx-auto" /> : <Home className="w-8 h-8 mx-auto" />}
          </div>
          Nothing to show for this period.
        </div>
      ) : (
        <div style={{ border: `0.5px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 640 }}>
              <thead>
                <tr style={{ background: C.teal }}>
                  {columns.map((c, i) => (
                    <th
                      key={c}
                      style={{
                        textAlign: i === 0 ? 'left' : 'left',
                        padding: '10px 12px',
                        fontWeight: 500,
                        color: C.darkGreen,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const linkable =
                    (r.kind === 'lead' && r.contact_id) ||
                    (r.kind !== 'lead' && !String(r.id ?? '').startsWith('rea-'));
                  return (
                    <tr
                      key={r.id || i}
                      onClick={linkable ? () => openRow(r) : undefined}
                      style={{
                        borderTop: `0.5px solid ${C.border}`,
                        background: i % 2 === 1 ? C.surface1 : undefined,
                        cursor: linkable ? 'pointer' : 'default',
                      }}
                      onMouseEnter={linkable ? (e) => { e.currentTarget.style.background = C.greenLight; } : undefined}
                      onMouseLeave={linkable ? (e) => { e.currentTarget.style.background = i % 2 === 1 ? C.surface1 : '#fff'; } : undefined}
                    >
                      {kind === 'lead' ? (
                        <>
                          <Td primary>{r.name}</Td>
                          <Td muted>{r.address || '—'}</Td>
                          <Td>{r.stage || '—'}</Td>
                          <Td>{r.priority || '—'}</Td>
                          <Td muted>{r.source || '—'}</Td>
                          <Td muted>{r.agent_name || '—'}</Td>
                          <Td muted>{fmtDate(r.date)}</Td>
                          {canDelete && (
                            <Td style={{ textAlign: 'right', width: 44 }}>
                              <button
                                onClick={(e) => handleDelete(r, e)}
                                disabled={deletingId === r.id}
                                title="Delete lead"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: deletingId === r.id ? 'default' : 'pointer',
                                  color: C.red,
                                  opacity: deletingId === r.id ? 0.4 : 1,
                                  padding: 4,
                                  display: 'inline-flex',
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Td>
                          )}
                        </>
                      ) : kind === 'sold' ? (
                        <>
                          <Td primary>{r.address}</Td>
                          <Td muted>{r.agent_name || '—'}</Td>
                          <Td style={{ color: C.green, fontWeight: 600 }}>{fmtMoney(r.amount)}</Td>
                          <Td muted>{fmtDate(r.date)}</Td>
                        </>
                      ) : (
                        <>
                          <Td primary>{r.address}</Td>
                          <Td muted>{r.suburb || '—'}</Td>
                          <Td muted style={{ textTransform: 'capitalize' }}>{r.property_type || '—'}</Td>
                          <Td>{r.price ? fmtMoney(r.price) : '—'}</Td>
                          <Td muted>{r.status || '—'}</Td>
                          <Td muted>{r.agent_name || '—'}</Td>
                          <Td muted>{fmtDate(r.date)}</Td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const Td = ({ children, primary, muted, style }) => (
  <td
    style={{
      padding: '9px 12px',
      color: primary ? C.textPrimary : muted ? C.textSecondary : C.textPrimary,
      fontWeight: primary ? 600 : 400,
      whiteSpace: 'nowrap',
      maxWidth: 320,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...style,
    }}
  >
    {children}
  </td>
);

export default LeaderboardStatPage;
